export type TNotificationTab = "all" | "mentions";

export type TNotificationViewMode = "active" | "archived" | "snoozed";

export interface TNotificationFilters {
  view: TNotificationViewMode;
  unreadOnly: boolean;
}

interface TNotificationTriggeredBy {
  id?: string;
  display_name?: string;
  first_name?: string;
  avatar_url?: string | null;
}

interface TNotificationIssueRef {
  id?: string;
  identifier?: string;
  sequence_id?: number;
  name?: string;
  state_name?: string;
  state_group?: string;
}

interface TNotificationActivity {
  field?: string;
  verb?: "created" | "updated" | "deleted";
  new_value?: string | null;
  old_value?: string | null;
  issue_comment?: string | null;
}

interface TNotificationEntityRef {
  id?: string;
  name?: string;
}

/**
 * A single entry in a `field_change` notification's `changes[]` list. Scalar
 * fields carry `old_value`/`new_value`; collection (M2M) fields carry
 * `added`/`removed` display-name lists. Mirrors the backend
 * `notify_field_change` contract (cognota/notifications/senders.py).
 *
 * The senders disagree on where the human label lives, so **always read a label
 * through `changeLabel` (notification-summary.ts)** rather than `field`
 * directly:
 *
 * - `core/services/project_notifications.py`, `program_notifications.py` and
 *   `project_health.py` put the RAW column key in `field` — the field's **UUID**
 *   for a custom field — and the authoritative label in `field_label`.
 * - `work_items/service_changes.py`, `work_items/views/custom_fields.py` and
 *   `notifications/tasks.py::resolve_request_field_changes` resolve the label
 *   into `field` itself and send no `field_label`.
 *
 * Converging the six senders on one shape is a follow-up; until then a reader
 * that touches `field` alone is wrong for half of them. The digest email reads
 * the same order (`digest_render.py::_change_row`).
 */
export interface TNotificationChange {
  field: string;
  /** Authoritative human label, when the sender ships one. */
  field_label?: string;
  old_value?: string | null;
  new_value?: string | null;
  added?: string[];
  removed?: string[];
  /** A work item's colour change carries the stored `#rrggbb` beside the name
   *  (NG-1595). Only `work_items/service_changes.py` sends these, and only for
   *  `color`: the reader here draws the swatch from them and names the colour
   *  from its own localised palette, while `old_value`/`new_value` hold the
   *  English name the digest email prints. Absent on every other field. */
  old_swatch?: string | null;
  new_swatch?: string | null;
}

export interface TNotification {
  id: string;
  title?: string;
  sender?: string;
  entity_identifier?: string;
  entity_name?: string;
  project?: string;
  workspace?: string;
  read_at: string | null;
  archived_at: string | null;
  snoozed_till: string | null;
  is_inbox_issue?: boolean;
  is_mentioned_notification?: boolean;
  created_at?: string;
  triggered_by_details?: TNotificationTriggeredBy;
  data?: {
    issue?: TNotificationIssueRef;
    issue_activity?: TNotificationActivity;
    request?: TNotificationEntityRef;
    project?: TNotificationEntityRef;
    program?: TNotificationEntityRef;
    goal?: TNotificationEntityRef;
    work_item?: TNotificationEntityRef;
    report?: TNotificationEntityRef;
    dashboard?: TNotificationEntityRef;
    /** A team the notification is about: the account senders (membership, the
     *  root-team Home reassignment outcome, NG-2156). */
    team?: TNotificationEntityRef;
    /** A stopped automation (NG-3682, `in_app:automation:target_broken`): whose
     *  rules stopped, which ones, and the status that broke them. */
    owner?: { scope: "workspace" | "team" | "project"; id?: string; name?: string; teams?: number };
    rules?: string[];
    status?: { name: string; kind: string; deleted: boolean };
    changed_by?: string | null;
    /** The root-team reassignment outcome's counts (NG-2156): `total` re-homed,
     *  or `failed` that could not be. */
    total?: number;
    failed?: number;
    // The two-category change model (cognota/notifications/senders.py).
    // status_change stamps the single field diff flat on data
    // (field/old_value/new_value); field_change carries a grouped `changes[]`
    // list. Both stamp `changed_at` (UTC ISO).
    field?: string;
    old_value?: string | null;
    new_value?: string | null;
    changes?: TNotificationChange[];
    changed_at?: string;
    health?: string;
    target_date?: string | null;
    comment?: { id?: string; excerpt?: string };
    // Workflow-runtime arrivals (`in_app:request:{page_ready,review_requested}`,
    // cognota/notifications/senders.py). `page_name` is the workflow step the
    // request landed on; `message` is the body the workflow author wrote on that
    // step's notification config (blank when they left it empty).
    page_name?: string;
    message?: string;
    added_user_ids?: string[];
    // Async CSV export ("export ready" report notification): the presigned S3
    // download URL the worker minted on completion. Present only on the
    // `in_app:report:export_ready` variant (the FE renders it as the CTA).
    url?: string;
    // Account notifications (entity_name "user"/"team"/"workspace",
    // cognota/notifications/senders.py). A lead/admin editing a member's
    // profile ships the changed fields; scheduling time off ships the entry;
    // a role change ships the resolved role labels. The workspace object names
    // the workspace the change happened in (distinct from the top-level
    // `workspace` id).
    changed_fields?: TNotificationChange[];
    time_off?: {
      id?: string;
      title?: string;
      start_date?: string | null;
      end_date?: string | null;
    };
    old_role_label?: string;
    new_role_label?: string;
    workspace?: { id?: string; name?: string };
  };
}

/** Notifications whose entity is an intake request (submission) rather than a
 * work item — rendered through the entity-aware row/detail paths. */
function isRequestNotification(notification: TNotification): boolean {
  return notification.entity_name === "request";
}

/** Project-level notifications (state/field/health/deleted). */
function isProjectNotification(notification: TNotification): boolean {
  return notification.entity_name === "project";
}

/** Program-level notifications (status/field change). */
function isProgramNotification(notification: TNotification): boolean {
  return notification.entity_name === "program";
}

/** Goal-level notifications (NG-617: lead assigned/unassigned, status change). */
function isGoalNotification(notification: TNotification): boolean {
  return notification.entity_name === "goal";
}

/** Work-item-level notifications (status/field/deleted/due). */
function isWorkItemNotification(notification: TNotification): boolean {
  return notification.entity_name === "work_item";
}

/** Report-level notifications — a report run finished (complete/failed) or an
 * async CSV export is ready. The run-complete sender deep-links to the Report
 * View; mirrors the BE `notify_report_*` contract
 * (cognota/notifications/senders.py). */
function isReportNotification(notification: TNotification): boolean {
  return notification.entity_name === "report";
}

/** The "export ready" report variant — sender `in_app:report:export_ready`, the
 * async CSV export's worker-fired notification. Distinct from the run-complete
 * report notification (same `entity_name`) by its sender; it carries a presigned
 * `data.url`, so its CTA is a Download link rather than the report deep-link. */
export function isReportExportReadyNotification(notification: TNotification): boolean {
  return (
    isReportNotification(notification) && (notification.sender ?? "").endsWith(":export_ready")
  );
}

/** Status-change notifications — sender `in_app:{resource}:status_change`.
 * The single field diff lives flat on `data` (field/old_value/new_value). */
export function isStatusChange(notification: TNotification): boolean {
  return (notification.sender ?? "").endsWith(":status_change");
}

/** Field-change notifications — sender `in_app:{resource}:field_change`. The
 * grouped diff lives on `data.changes[]`. */
export function isFieldChange(notification: TNotification): boolean {
  return (notification.sender ?? "").endsWith(":field_change");
}

/** Notifications rendered by the custom entity-detail path (email-style React
 * display) rather than the live work-item detail: requests, projects, and
 * work items, and reports. */
export function isEntityNotification(notification: TNotification): boolean {
  return (
    isRequestNotification(notification) ||
    isProjectNotification(notification) ||
    isProgramNotification(notification) ||
    isGoalNotification(notification) ||
    isWorkItemNotification(notification) ||
    isReportNotification(notification)
  );
}

/** Account notifications — informational "someone changed your details" FYIs
 * whose entity is the user / their team / the workspace rather than a work
 * object: a lead or admin editing a member's profile or scheduling time off
 * (#2814), a role change, or being added to a workspace/team. They carry a
 * complete backend-authored title and no deep-link CTA (the change is
 * informational), so they render through the account-detail path rather than
 * the entity-detail deep-link path. Mirrors the `entity_name`s the account
 * senders stamp in cognota/notifications/senders.py. */
export function isAccountNotification(notification: TNotification): boolean {
  return (
    notification.entity_name === "user" ||
    notification.entity_name === "team" ||
    notification.entity_name === "workspace"
  );
}

export interface TUnreadNotificationCount {
  total_unread_notifications_count: number;
  mention_unread_notifications_count: number;
}

export interface TNotificationPagination {
  next_cursor: string | null;
  prev_cursor: string | null;
  next_page_results: boolean;
  total_results: number;
}

export interface TNotificationListResponse extends TNotificationPagination {
  results: TNotification[];
}

export interface TNotificationQuery {
  type?: string;
  snoozed?: boolean;
  archived?: boolean;
  mentioned?: boolean;
  read?: boolean;
  per_page?: number;
  cursor?: string;
}

export interface TNotificationUpdatePayload {
  snoozed_till: string | null;
}

type TMarkAllReadTypeScope = "all";

export interface TNotificationMarkAllPayload {
  type: TMarkAllReadTypeScope;
  snoozed: boolean;
  archived: boolean;
  read: boolean;
}

export const DEFAULT_NOTIFICATION_FILTERS: TNotificationFilters = {
  view: "active",
  unreadOnly: false,
};

export const NOTIFICATION_PAGE_SIZE = 30;
